import {
  BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, Cell,
} from 'recharts'
import type { AdminMetrics } from '../../types'

interface PostStatusChartProps {
  metrics: AdminMetrics
}

export function PostStatusChart({ metrics }: PostStatusChartProps) {
  const data = [
    { name: 'Published', value: metrics.publishedPosts, color: '#16a34a' },
    { name: 'Draft',     value: metrics.draftPosts,     color: '#d97706' },
  ]
  const total = metrics.totalPosts || 1
  const publishedPct = Math.round((metrics.publishedPosts / total) * 100)

  return (
    <>
      <div className="sc-card">
        <div className="sc-head">
          <div className="sc-head-left">
            <span className="sc-head-icon">📝</span>
            <div>
              <h3 className="sc-head-title">Posts by Status</h3>
              <p className="sc-head-sub">Published vs draft breakdown</p>
            </div>
          </div>
          <span className="sc-head-badge">{metrics.totalPosts} total</span>
        </div>

        <div className="sc-body">
          <div className="sc-chart">
            <ResponsiveContainer width="100%" height={160}>
              <BarChart data={data} barCategoryGap="40%">
                <XAxis
                  dataKey="name"
                  tick={{ fill: '#94a3b8', fontSize: 12, fontWeight: 500 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  allowDecimals={false}
                  tick={{ fill: '#94a3b8', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                  width={28}
                />
                <Tooltip
                  contentStyle={{
                    background: '#1e293b',
                    border: '1px solid #334155',
                    borderRadius: 8,
                    fontSize: 13,
                    color: '#f1f5f9',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                  }}
                  cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {data.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="sc-stats">
            {data.map(d => (
              <div key={d.name} className="sc-stat">
                <span className="sc-stat-dot" style={{ background: d.color }} />
                <span className="sc-stat-label">{d.name}</span>
                <span className="sc-stat-val">{d.value}</span>
              </div>
            ))}
            <div className="sc-progress-wrap">
              <div className="sc-progress-track">
                <div
                  className="sc-progress-fill"
                  style={{ width: `${publishedPct}%` }}
                  title={`${publishedPct}% published`}
                />
              </div>
              <span className="sc-progress-label">{publishedPct}% published</span>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .sc-card {
          background: var(--bg-card);
          border: 1px solid var(--border);
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 1px 4px rgba(0,0,0,0.06);
        }
        .sc-head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 16px 20px;
          gap: 12px;
          background: linear-gradient(135deg, #1e3a5f 0%, #1e40af 100%);
          border-bottom: 1px solid rgba(255,255,255,0.08);
        }
        .sc-head-left  { display: flex; align-items: center; gap: 12px; }
        .sc-head-icon  { font-size: 1.4rem; flex-shrink: 0; }
        .sc-head-title { font-size: 0.9rem; font-weight: 700; color: #fff; margin: 0 0 2px; }
        .sc-head-sub   { font-size: 0.72rem; color: rgba(255,255,255,0.72); margin: 0; }
        .sc-head-badge {
          font-size: 0.72rem;
          font-weight: 600;
          color: rgba(255,255,255,0.9);
          background: rgba(255,255,255,0.15);
          border: 1px solid rgba(255,255,255,0.25);
          border-radius: 20px;
          padding: 3px 10px;
          white-space: nowrap;
          flex-shrink: 0;
        }

        .sc-body {
          padding: 16px 20px;
          display: flex;
          flex-direction: column;
          gap: 14px;
        }
        .sc-chart { width: 100%; }

        .sc-stats {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .sc-stat {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 0.82rem;
        }
        .sc-stat-dot {
          width: 9px; height: 9px;
          border-radius: 50%;
          flex-shrink: 0;
        }
        .sc-stat-label { color: var(--text-secondary); flex: 1; }
        .sc-stat-val   { font-weight: 700; color: var(--text-primary); font-variant-numeric: tabular-nums; }

        .sc-progress-wrap {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-top: 4px;
        }
        .sc-progress-track {
          flex: 1;
          height: 6px;
          background: rgba(22,163,74,0.15);
          border-radius: 3px;
          overflow: hidden;
        }
        .sc-progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #16a34a, #4ade80);
          border-radius: 3px;
          transition: width 0.4s ease;
        }
        .sc-progress-label {
          font-size: 0.7rem;
          color: #16a34a;
          font-weight: 600;
          white-space: nowrap;
        }
      `}</style>
    </>
  )
}
